<p-dialog
    [header]="'Generate default icon + banner with AI'"
    [visible]="visible()"
    (visibleChange)="onVisibleChange($event)"
    [style]="{ width: '60rem' }"
    [breakpoints]="{ '1280px': '95vw' }"
    [modal]="true"
    [draggable]="false"
    appendTo="body"
>
    <div class="flex flex-col gap-4">
        <p class="text-sm text-muted-color">
            The default icon and banner carry the game title. They are generated
            in a reference site template's style — its base art is used as the
            plate, or generated first if missing — and saved as a reusable
            finalized variant.
        </p>

        <!-- Reference site template (template mode only; realm mode is fixed) -->
        @if (!lockedRef()) {
        <div class="flex flex-col gap-2">
            <label class="font-medium">Reference site template</label>
            @if (preparing()) {
            <div class="flex items-center gap-2 text-muted-color text-sm py-2">
                <i class="pi pi-spin pi-spinner"></i>
                <span>Loading templates…</span>
            </div>
            } @else {
            <p-select
                [options]="refOptions()"
                optionLabel="label"
                optionValue="value"
                [ngModel]="refSiteTemplateId()"
                (ngModelChange)="refSiteTemplateId.set($event ?? null)"
                placeholder="Select a reference template"
                appendTo="body"
                [style]="{ width: '100%' }"
            />
            }
        </div>
        }

        @if (blockReason()) {
        <p-message severity="warn" [text]="blockReason()!" />
        } @else {
        <div>
            <p-button
                label="Generate"
                icon="pi pi-microchip-ai"
                [disabled]="working()"
                (onClick)="generate()"
            />
        </div>
        }

        <!-- Plates -->
        @for (asset of ['icon', 'banner']; track asset) {
        <div
            class="flex flex-col gap-2 pt-3 border-t border-surface-200 dark:border-surface-700"
        >
            <div class="flex items-center justify-between">
                <span class="font-medium capitalize">{{ asset }}</span>
                @if (plate($any(asset)).status === 'done' || plate($any(asset)).status
                === 'error') {
                <p-button
                    label="Regenerate"
                    icon="pi pi-refresh"
                    size="small"
                    severity="secondary"
                    text
                    [disabled]="working() || saving()"
                    (onClick)="regenerate($any(asset))"
                />
                }
            </div>

            @switch (plate($any(asset)).status) { @case ('working') {
            <div class="flex items-center gap-2 text-muted-color text-sm py-2">
                <p-progress-spinner
                    [style]="{ width: '1.5rem', height: '1.5rem' }"
                    strokeWidth="6"
                />
                <span>{{ plate($any(asset)).statusText }}</span>
            </div>
            } @case ('error') {
            <p-message severity="error" [text]="plate($any(asset)).error!" />
            } @case ('done') {
            <div class="flex flex-wrap gap-3">
                @for (cand of plate($any(asset)).candidates; track cand.url) {
                <button
                    type="button"
                    class="rounded border-2 overflow-hidden transition-colors relative"
                    [ngClass]="
                        isSelected($any(asset), cand.url)
                            ? 'border-primary'
                            : 'border-surface-200 dark:border-surface-700 hover:border-primary/50'
                    "
                    [disabled]="saving()"
                    (click)="pick($any(asset), cand)"
                >
                    <img
                        [src]="cand.url"
                        [alt]="asset + ' candidate'"
                        class="block object-cover"
                        [ngClass]="asset === 'banner' ? 'w-56 h-32' : 'w-32 h-32'"
                    />
                    <i
                        class="pi pi-search-plus absolute top-1 left-1 bg-black/50 text-white rounded p-1 text-xs"
                        title="View large"
                        (click)="
                            preview.open(cand.url, asset);
                            $event.stopPropagation()
                        "
                    ></i>
                    @if (isSelected($any(asset), cand.url)) {
                    <span
                        class="absolute top-1 right-1 bg-primary text-primary-contrast rounded-full w-5 h-5 flex items-center justify-center"
                    >
                        <i class="pi pi-check text-xs"></i>
                    </span>
                    }
                </button>
                } @empty {
                <span class="text-sm text-muted-color italic"
                    >No candidates returned.</span
                >
                }
            </div>
            } @default {
            <span class="text-sm text-muted-color italic"
                >Not generated yet.</span
            >
            } }
        </div>
        }
    </div>

    <ng-template #footer>
        <div class="flex justify-end gap-2">
            <p-button
                label="Cancel"
                severity="secondary"
                outlined
                [disabled]="saving()"
                (onClick)="close()"
            />
            <p-button
                label="Save & apply"
                icon="pi pi-check"
                [loading]="saving()"
                [disabled]="!canSave()"
                (onClick)="save()"
            />
        </div>
    </ng-template>
</p-dialog>
